import { EventType } from "@/analytics/analytics-events";
import { MoveDataSetToSheetProperties } from "@/analytics/data-organization-events-properties";
import { updateProject } from "@/components/common/project-provider/update-project";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { changeMode } from "@/store/mode-slice";
import { selectAreaFor } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectIElement } from "@faro-lotv/app-component-toolbox";
import {
  Dropdown,
  FaroMenuItem,
  FaroText,
  MoveElementIcon,
  NoTranslate,
  Option,
  TranslateVar,
  TruncatedFaroText,
  useDialog,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElementAreaSection,
  isIElementAreaSection,
} from "@faro-lotv/ielement-types";
import {
  selectAllIElementsOfType,
  selectIsVideoWalkDataSession,
} from "@faro-lotv/project-source";
import {
  createMutationMoveDataSession,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Box } from "@mui/material";
import { useCallback, useMemo, useRef, useState, type JSX } from "react";
import { shallowEqual } from "react-redux";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";

export const MOVE_DATA_SESSION_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.moveDataSession,
  CustomComponent: MoveDataSessionAction,
};

type MoveDataSessionActionProps = {
  /** The id of the element to move */
  elementId: GUID;

  /** Callback to close the context menu after the action is performed */
  closeContextMenu(): void;

  /** Whether to use dark mode styling */
  dark?: boolean;
};

/**
 * Action to move a data session to a different area. Renders a custom component to show a dropdown with the available areas.
 *
 * @returns A menu item that opens a dialog to move the data session when clicked
 */
export function MoveDataSessionAction({
  elementId,
  closeContextMenu,
  dark,
}: MoveDataSessionActionProps): JSX.Element | null {
  const element = useAppSelector(selectIElement(elementId));

  const currentArea = useAppSelector(selectAreaFor(element));
  const allAreas = useAppSelector(
    selectAllIElementsOfType(isIElementAreaSection),
    shallowEqual,
  );

  const targetMode = useAppSelector(selectIsVideoWalkDataSession(elementId))
    ? "sheet"
    : "overview";

  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();

  const { openToast } = useToast();
  const { createDialog, setConfirmButtonDisabled } = useDialog();
  const { handleErrorWithDialog } = useErrorHandlers();

  const targetArea = useRef<IElementAreaSection | undefined>(undefined);

  const onTargetChanged = useCallback(
    (value: IElementAreaSection | undefined) => {
      if (!currentArea) {
        openToast({ title: "The element cannot be moved", variant: "error" });
        return;
      }

      targetArea.current = value;
      setConfirmButtonDisabled(!value || value.id === currentArea.id);
    },
    [currentArea, openToast, setConfirmButtonDisabled],
  );

  const onConfirmDialog = useCallback(async () => {
    if (!element) return false;

    if (!currentArea) {
      openToast({ title: "The element cannot be moved", variant: "error" });
      return false;
    }

    if (!targetArea.current) return false;

    Analytics.track<MoveDataSetToSheetProperties>(
      EventType.moveDataSetToSheet,
      {
        elementType: element.type,
        // Need to nullish coalesced to undefined here because null is not allowed
        elementTypeHint: element.typeHint ?? undefined,
      },
    );

    const targetAreaId = targetArea.current.id;

    try {
      await projectApiClient.applyMutations([
        createMutationMoveDataSession(element.id, targetAreaId),
      ]);

      // Fetch the changed sub-tree and update the local copy of the project
      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            ancestorIds: [currentArea.id, targetAreaId],
          },
        }),
      );

      // The app doesn't behave well when elements change positions.
      // Instead try to open the overview at its new location to avoid the camera being stuck in the void.
      dispatch(setActiveElement(element.id));
      dispatch(changeMode(targetMode));

      openToast({
        title: (
          <>
            <TranslateVar name="elementName">{element.name}</TranslateVar> moved
            to{" "}
            <TranslateVar name="targetArea">
              {targetArea.current.name}
            </TranslateVar>
          </>
        ),
        message: "Make sure your data set is still aligned correctly",
      });

      return true;
    } catch (error) {
      handleErrorWithDialog({
        title: "Unable to move Element",
        error,
      });
      return false;
    } finally {
      closeContextMenu();
    }
  }, [
    element,
    projectApiClient,
    dispatch,
    currentArea,
    targetMode,
    openToast,
    handleErrorWithDialog,
    closeContextMenu,
  ]);

  const moveDataSession = useCallback(async () => {
    if (!element) return;

    if (!currentArea) {
      openToast({ title: "The element cannot be moved", variant: "error" });
      return;
    }

    targetArea.current = undefined;

    // Create edit confirmation dialog
    await createDialog({
      title: (
        <>
          Move <TranslateVar name="elementName">{element.name}</TranslateVar>
        </>
      ),
      confirmText: "Move",
      isConfirmDisabled: true,
      content: (
        <MoveDataSessionForm
          onTargetChanged={onTargetChanged}
          currentArea={currentArea}
          allAreas={allAreas}
          hasDataSetWorldPose={!!element.pose?.isWorldPose}
        />
      ),
      allowToCloseWithoutCancelling: true,
      size: "m",
      showXButton: true,
      onConfirm: onConfirmDialog,
    });
  }, [
    element,
    currentArea,
    createDialog,
    onTargetChanged,
    allAreas,
    onConfirmDialog,
    openToast,
  ]);

  return (
    <FaroMenuItem
      label="Move to different area"
      onClick={moveDataSession}
      Icon={MoveElementIcon}
      dark={dark}
    />
  );
}

type MoveDataSessionFormProps = {
  /** Callback executed when the target changed */
  onTargetChanged(iElement: IElementAreaSection | undefined): void;

  /** The current area of the iElement */
  currentArea: IElementAreaSection;

  /** The list of all available areas the element can be moved into */
  allAreas: IElementAreaSection[];

  /** Whether the moved data set has a world pose (used to show an alignment hint) */
  hasDataSetWorldPose: boolean;
};

function MoveDataSessionForm({
  onTargetChanged,
  currentArea,
  allAreas,
  hasDataSetWorldPose,
}: MoveDataSessionFormProps): JSX.Element {
  const [selectedArea, setSelectedArea] = useState<
    IElementAreaSection | undefined
  >(currentArea);

  const options: Option[] = useMemo(
    () =>
      allAreas.map((area) => ({
        label: <NoTranslate>{area.name}</NoTranslate>,
        value: area.id,
        key: area.id,
      })),
    [allAreas],
  );

  // The alignment will only be preserved by the mutation, if the data is referenced to the world in both locations
  const willLoseAlignment =
    selectedArea !== currentArea &&
    (!selectedArea?.pose?.isWorldPose || !currentArea.pose?.isWorldPose) &&
    !hasDataSetWorldPose;

  return (
    <>
      <Box component="div" sx={{ mb: 3 }}>
        <TruncatedFaroText variant="bodyM">
          Current area:{" "}
          <b>
            <TranslateVar name="areaName">{currentArea.name}</TranslateVar>
          </b>
        </TruncatedFaroText>
        {willLoseAlignment && (
          <FaroText variant="bodyM">
            By moving the data you will lose its alignment.
          </FaroText>
        )}
      </Box>

      <Dropdown
        label="Move to area"
        fullWidth
        value={selectedArea?.id}
        options={options}
        onChange={(ev) => {
          const area = allAreas.find((area) => area.id === ev.target.value);
          setSelectedArea(area);
          onTargetChanged(area);
        }}
      />
    </>
  );
}
